import axios from 'axios'
import Raven from 'raven-js'

import store from '@/store'
import poll from '@/helpers/externals/poll'

const checkoutURL = '/7.0/carwash/checkouts/'
const cartURL = '/8.0/carwash/carts/'
const promocodeURL = '/7.0/carwash/promocode/'
const orderURL = '/7.0/carwash/orders/'

const ORDER_STATES = {
  waiting3DS: 'waiting_3DS',
  fail: 'fail',
  success: 'success',
}

const retrievePromocode = async (code, cartId) => {
  try {
    const payload = {
      cart: cartId
    }
    const promocode = await axios({
      url: `${promocodeURL + code}/`,
      method: 'GET',
      params: payload
    })
    store.commit('Cart/setPromocode', promocode.data)
  } catch (err) {

    if (err.response.status === 404) return null

    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }

    throw err
  }
}

const createCart = async () => {
  try {
    const cart = await axios.post(cartURL)
    store.commit('Cart/setCartId', cart.data.pk)
  } catch (err) {
    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const retrieveCart = async (cartId) => {
  try {
    const result = await axios.get(`${cartURL + cartId}/`)
    store.commit('Cart/setBackendCart', result.data)
  } catch (err) {
    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const updateCart = async (cartId, payload) => {
  try {
    const form = new FormData()
    for (const key in payload) {
      form.append(key, payload[key])
    }
    const result = await axios({
      url: `${cartURL + cartId}/`,
      method: 'PUT',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: form
    })
    store.commit('Cart/setBackendCart', result.data)
  } catch (err) {
    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const addItem = async (cartId, itemId, itemType) => {
  try {
    const form = new FormData()
    let type = itemType
    if (itemType === 'creditsPack') {
      type = 'deal'
    }
    form.append('item_type', type)
    form.append('reference', itemId)
    form.append('quantity', 1)
    const cartItem = await axios({
      url: `${cartURL + cartId}/items/`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: form
    })
    if (itemType === 'booking') {
      store.commit('Cart/setBookingCartItemId', { bookingId: itemId, cartItemId: cartItem.data.pk })
    } else if (itemType === 'creditsPack') {
      store.commit('Cart/setCreditsPackCartItemId', { creditsPackId: itemId, cartItemId: cartItem.data.pk })
      // return the price
      return cartItem.data.fields.price_incl_vat
    }
  } catch (err) {
    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const removeBookingItem = (cartId, cartItemId) => {
  axios.delete(`${cartURL + cartId}/items/${cartItemId}/`)
}

const listPaymentMethods = async (cartId) => {
  try {
    // api doc says it's a post but in reality it's a get
    const result = await axios.get(`${cartURL + cartId}/allowed-payment-methods/`)
    return result.data.payment_methods
  } catch (err) {
    console.log(err)
    if (!(err.response && err.response.status === 401)) {
      Raven.captureException(err)
    }
    throw err
  }
}

const checkoutCart = async (cartId, paymentMethod) => {
  const form = new FormData()
  form.append('payment_method', paymentMethod)
  form.append('java_enabled', window.navigator.javaEnabled())
  form.append('language', window.navigator.language)
  form.append('color_depth', window.screen.colorDepth)
  form.append('screen_width', window.screen.width)
  form.append('screen_height', window.screen.height)
  form.append('timezone_offset', new Date().getTimezoneOffset().toString())
  form.append('user_agent', window.navigator.userAgent)
  form.append('javascript_enabled', true)

  const checkout = await axios.post(`${cartURL + cartId}/checkout/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  const checkoutId = checkout.data.pk
  await poll({
    fn: () => axios.get(`${checkoutURL + checkoutId}/`),
    validate: result => result.data.fields && result.data.fields.state === 'ready',
    interval: 500,
    maxAttempts: 16
  })
  await axios.post(`${checkoutURL + checkoutId}/complete/`)
  const order = await poll({
    fn: () => axios.get(`${checkoutURL + checkoutId}/`),
    validate: result => result.data.fields && result.data.fields.state === 'ordered',
    interval: 500,
    maxAttempts: 16
  })
  const orderId = order.data.fields.order_id
  const result = await poll({
    fn: () => axios.get(`${orderURL + orderId}/`),
    validate: result => result.data.fields && (result.data.fields.status === ORDER_STATES.success || result.data.fields.status === ORDER_STATES.fail || result.data.fields.status === ORDER_STATES.waiting3DS),
    interval: 500,
    maxAttempts: 16
  })
  if (result.data.fields.status === ORDER_STATES.waiting3DS) {
    return { redirect: result.data.fields.redirect_url_3DS, orderId }
  }
  if (result.data.fields.status === ORDER_STATES.success) {
    return true
  }
  if (result.data.fields.status === ORDER_STATES.fail) {
    throw new Error()
  }
}

const check3DSsuccess = async (orderId) => {
  try {
    const result = await poll({
      fn: () => axios.get(`${orderURL + orderId}/`),
      validate: result => result.data.fields && (result.data.fields.status === ORDER_STATES.success || result.data.fields.status === ORDER_STATES.fail),
      interval: 1000,
      maxAttempts: 20
    })
    if (result.data.fields.status === ORDER_STATES.success) {
      return true
    } else {
      throw new Error('Le paiement n\'a pas été validé')
    }
  } catch (err) {
    console.error(err)
    const result = await orderForceUpdate(orderId)
    if (result === true) {
      return true
    } else {
      throw new Error('Une erreur est survenue lors du paiement, merci de contacter le support technique.')
    }
  }
}

const orderForceUpdate = async (orderId) => {
  try {
    const order = await axios.put(`${orderURL + orderId}/force-update/`)
    const orderStatus = order.data.fields.status
    if (orderStatus === ORDER_STATES.success) {
      return true
    } else {
      throw new Error('Le paiement n\'a pas été validé, veuillez réessayer plus tard ou contacter le support technique.')
    }
  } catch (err) {
    console.error(err)
    throw new Error('Une erreur est survenue lors du paiement, merci de contacter le support technique.')
  }
}

export {
  retrievePromocode,
  createCart,
  addItem,
  retrieveCart,
  updateCart,
  removeBookingItem,
  listPaymentMethods,
  checkoutCart,
  check3DSsuccess
}